/**
 * Organization validation schemas — Zod.
 * Shared with edge functions where applicable.
 */
import { z } from 'zod'
import { compactSvNit, formatSvNit, isValidSvNit, SV_NIT_DIGIT_LENGTH } from '../utils/sv-nit'
import { normalizeLegalEntityName } from '../utils/sv-identity'

export const organizationTaxIdSchema = z
  .string()
  .trim()
  .transform(formatSvNit)
  .superRefine((value, ctx) => {
    const digits = compactSvNit(value)
    if (!digits) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'validation.org_tax_id_required' })
      return
    }
    if (digits.length !== SV_NIT_DIGIT_LENGTH || !/^[019]\d+$/.test(digits)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'validation.org_tax_id_format' })
      return
    }
    if (!isValidSvNit(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'validation.org_tax_id_checksum' })
    }
  })

const organizationWebsiteSchema = z
  .string()
  .trim()
  .refine((value) => {
    if (!value || /\s/.test(value)) return false

    try {
      const url = new URL(value)
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return false

      const labels = url.hostname.toLowerCase().split('.')
      return labels.length >= 2 && labels.every((label) => label.length > 0)
    } catch {
      return false
    }
  }, 'validation.url_invalid')

export const createOrganizationSchema = z.object({
  name: z.string().trim().min(2, 'validation.org_name_min').max(100, 'validation.org_name_max'),
  legalName: z
    .string()
    .trim()
    .min(3, 'validation.org_legal_name_min')
    .max(200, 'validation.org_legal_name_max')
    .transform((value) => normalizeLegalEntityName(value).trim()),
  taxId: organizationTaxIdSchema,
  email: z.string().trim().email('validation.email_invalid'),
  phone: z
    .string()
    .trim()
    .min(8, 'validation.phone_min')
    .max(15, 'validation.phone_max')
    .optional(),
  countryId: z.string().default('SV'),
  cityId: z.string().optional(),
  insuranceModel: z
    .literal('fleet', {
      message: 'listing.validation.insurance_agency_only',
    })
    .default('fleet'),
})

export const updateOrganizationSchema = createOrganizationSchema.partial().extend({
  website: organizationWebsiteSchema.optional(),
  addressLine1: z.string().max(200).optional(),
  addressLine2: z.string().max(200).optional(),
  addressCity: z.string().max(100).optional(),
  addressRegion: z.string().max(100).optional(),
  addressPostalCode: z.string().max(10).optional(),
})

export const inviteMemberSchema = z
  .object({
    email: z.string().trim().email('validation.email_invalid').optional(),
    phone: z
      .string()
      .trim()
      .min(8, 'validation.phone_min')
      .max(15, 'validation.phone_max')
      .optional(),
    // Business rule: invitees are always fleet_driver. The legal rep
    // (sole fleet_manager) is set at org creation.
    role: z.literal('fleet_driver').default('fleet_driver'),
  })
  .refine((d) => d.email ?? d.phone, {
    message: 'validation.email_or_phone_required',
  })
